<template>
  <div class="container">
    <div class="columns box-title is-12">
      <div class="column is-6">
        <h4 class="has-text-left">
          <i class="fal fa-user-chart"></i>
          {{ $t('employeeRegistrationKpi.page_title') }}
          <span class="countTotal">({{ totalRows }})</span>
        </h4>
      </div>
      <div class="column is-6">
        <div style="float: right">
          <vs-button
              ref="button"
              flat
              class="form-btn-loading"
              style="margin-top: 0"
              :loading="exporting"
              :disabled="exporting || loading"
              @click="exportExcel"
          >
            {{ $t('employeeRegistrationKpi.export_button') }}
          </vs-button>
        </div>
      </div>
    </div>

    <div class="box">
      <div class="columns is-multiline">
        <div class="column is-12-mobile is-7-tablet is-6-desktop">
          <div class="control">
            <label class="label">{{ $t('employeeRegistrationKpi.filters.search') }}:</label>
            <input
                v-model="searchTerm"
                class="input"
                type="text"
                :placeholder="$t('employeeRegistrationKpi.filters.search_placeholder')"
                @input="onSearchInput"
            />
          </div>
        </div>
        <div class="column is-12-mobile is-5-tablet is-4-desktop">
          <div class="control">
            <label class="label">{{ $t('employeeRegistrationKpi.branch_filter_label') }}:</label>
            <multiselect
                v-model="selectedBranch"
                :options="branchOptions"
                label="name"
                track-by="id"
                :placeholder="$t('employeeRegistrationKpi.branch_filter_label')"
                :searchable="true"
                :allow-empty="false"
                :show-labels="false"
                :disabled="loading"
                @input="onFilterChange"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="center">
      <div v-if="loading" class="state-panel">
        <Loading/>
        <p>{{ $t('employeeRegistrationKpi.loading') }}</p>
      </div>

      <div v-else-if="errorMessage" class="state-panel error-panel">
        <p>{{ errorMessage }}</p>
        <vs-button flat danger @click="retryLoad">
          {{ $t('employeeRegistrationKpi.retry') }}
        </vs-button>
      </div>

      <div v-else-if="rows.length === 0" class="state-panel">
        <p>{{ $t('employeeRegistrationKpi.empty_state') }}</p>
      </div>

      <vs-table v-else>
        <template #thead>
          <vs-tr>
            <vs-th>{{ $t('employeeRegistrationKpi.table_columns.employee_code') }}</vs-th>
            <vs-th>{{ $t('employeeRegistrationKpi.table_columns.employee_name') }}</vs-th>
            <vs-th>{{ $t('employeeRegistrationKpi.table_columns.branch') }}</vs-th>
            <vs-th>{{ $t('employeeRegistrationKpi.table_columns.total_registered') }}</vs-th>
            <vs-th>{{ $t('employeeRegistrationKpi.table_columns.total_app_users') }}</vs-th>
            <vs-th>{{ $t('employeeRegistrationKpi.table_columns.conversion_rate') }}</vs-th>
            <vs-th>{{ $t('employeeRegistrationKpi.table_columns.actions') }}</vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr
              v-for="(row, index) in rows"
              :key="row.registered_by || index"
          >
            <vs-td>{{ employeeCode(row) }}</vs-td>
            <vs-td>{{ row.employee_name || placeholder }}</vs-td>
            <vs-td>{{ branchName(row) }}</vs-td>
            <vs-td>{{ row.total_registered }}</vs-td>
            <vs-td>{{ row.total_app_users }}</vs-td>
            <vs-td>{{ formatConversionRate(row.conversion_rate) }}</vs-td>
            <vs-td>
              <vs-button flat @click="openDetail(row, $event)">
                {{ $t('employeeRegistrationKpi.view_detail_button') }}
              </vs-button>
            </vs-td>
          </vs-tr>
        </template>
        <template #footer>
          <pagination
              v-if="paginator && paginator.last_page > 1"
              :pagination="paginator"
              :offset="10"
              @paginate="changePage"
          ></pagination>
        </template>
      </vs-table>
    </div>

    <DetailDrawer
        v-if="drawerOpen && selectedEmployee"
        :key="selectedEmployee.employeeId"
        :employee-id="selectedEmployee.employeeId"
        :employee-code="selectedEmployee.employeeCode"
        :employee-name="selectedEmployee.employeeName"
        :branch-name="selectedEmployee.branchName"
        @close="closeDetail"
    />
  </div>
</template>

<script>
import Loading from '../../Loading';
import pagination from '../../../Plugins/Paginations/Pagination';
import DetailDrawer from './DetailDrawer.vue';
import {
  downloadKpiAllExport,
  fetchBranches,
  fetchKpiAll
} from '@/services/employeeRegistrationKpiService';

export default {
  components: {
    DetailDrawer,
    Loading,
    pagination
  },
  data() {
    return {
      rows: [],
      paginator: null,
      branchOptions: [],
      selectedBranch: null,
      searchTerm: '',
      debouncedSearchTerm: '',
      searchDebounceTimer: null,
      loading: false,
      exporting: false,
      errorMessage: '',
      listRequestId: 0,
      drawerOpen: false,
      selectedEmployee: null,
      openerElement: null
    };
  },
  computed: {
    placeholder() {
      return this.$t('employeeRegistrationKpi.placeholder');
    },
    totalRows() {
      return this.paginator && this.paginator.total ? this.paginator.total : this.rows.length;
    }
  },
  methods: {
    async loadBranches() {
      const allOption = { id: null, name: this.$t('employeeRegistrationKpi.all_branches_option') };
      try {
        const branches = await fetchBranches();
        this.branchOptions = [allOption, ...branches];
      } catch (e) {
        this.branchOptions = [allOption];
      }
      this.selectedBranch = this.branchOptions[0];
    },
    buildListParams(page) {
      // Search is server-side via the `q` param; matches users.name OR users.user_card_id case-insensitively.
      // Page size is fixed at 10; admins can't adjust it from the UI.
      return {
        q: this.debouncedSearchTerm.trim() || undefined,
        branch_id: (this.selectedBranch && this.selectedBranch.id) || undefined,
        per_page: 10,
        page
      };
    },
    async loadKpi(page = 1) {
      const requestId = this.listRequestId + 1;
      this.listRequestId = requestId;
      this.loading = true;
      this.errorMessage = '';

      try {
        const response = await fetchKpiAll(this.buildListParams(page));
        if (requestId !== this.listRequestId) {
          return;
        }
        this.rows = this.getResponseRows(response);
        this.paginator = this.getResponsePaginator(response);
      } catch (e) {
        if (requestId === this.listRequestId) {
          this.rows = [];
          this.paginator = null;
          this.errorMessage = this.$t('employeeRegistrationKpi.error_loading');
        }
      } finally {
        if (requestId === this.listRequestId) {
          this.loading = false;
        }
      }
    },
    getResponseRows(response) {
      if (Array.isArray(response)) {
        return response;
      }

      if (response && Array.isArray(response.data)) {
        return response.data;
      }

      if (response && response.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }

      return [];
    },
    getResponsePaginator(response) {
      if (Array.isArray(response)) {
        return null;
      }

      if (response && response.data && Array.isArray(response.data.data)) {
        return response.data;
      }

      return response;
    },
    async onFilterChange() {
      if (this.paginator) {
        this.paginator.current_page = 1;
      }
      await this.loadKpi(1);
    },
    onSearchInput() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.debouncedSearchTerm = this.searchTerm;
        this.loadKpi(1);
      }, 300);
    },
    async retryLoad() {
      const page = this.paginator && this.paginator.current_page ? this.paginator.current_page : 1;
      await this.loadKpi(page);
    },
    async changePage(page) {
      await this.loadKpi(page);
    },
    employeeCode(row) {
      return row.user_card_id || this.placeholder;
    },
    branchName(row) {
      return row.branch_name || this.placeholder;
    },
    formatConversionRate(value) {
      return ((parseFloat(value) || 0) * 100).toFixed(2) + '%';
    },
    openDetail(row, event) {
      if (!row || row.registered_by === undefined || row.registered_by === null) {
        return;
      }

      this.openerElement = event && event.currentTarget ? event.currentTarget : null;
      this.selectedEmployee = {
        employeeId: row.registered_by,
        employeeCode: row.user_card_id || null,
        employeeName: row.employee_name || this.placeholder,
        branchName: this.branchName(row)
      };
      this.drawerOpen = true;
    },
    closeDetail() {
      this.drawerOpen = false;
      this.selectedEmployee = null;
      this.$nextTick(() => {
        if (this.openerElement && typeof this.openerElement.focus === 'function') {
          this.openerElement.focus();
        }
      });
    },
    async exportExcel() {
      if (this.exporting || this.loading) {
        return;
      }

      this.exporting = true;
      try {
        const {blob, filename} = await downloadKpiAllExport({
          q: this.debouncedSearchTerm.trim() || undefined,
          branch_id: (this.selectedBranch && this.selectedBranch.id) || undefined
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        this.$vs.notification({
          color: 'danger',
          position: 'top-right',
          title: this.$t('employeeRegistrationKpi.error_toast_export_failed'),
          text: this.$t('employeeRegistrationKpi.error_toast_export_failed')
        });
      } finally {
        this.exporting = false;
      }
    }
  },
  created() {
    this.loadBranches();
    this.loadKpi(1);
  },
  beforeDestroy() {
    clearTimeout(this.searchDebounceTimer);
  }
};
</script>

<style lang="scss" scoped>
.container {
  margin-top: 40px;

  .title-login {
    font-size: 20px;
    color: $primary-color;
  }
}

.state-panel {
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  color: #555;
}

.error-panel {
  border-color: #f4b5b5;
  color: #b42318;
}

</style>
